import type { AllowedPairing, RelationshipType } from '../types';

const RELATIONSHIP_NAME_PATTERN = /^[A-Z]+(?:_[A-Z]+)*$/;

export type RelationshipNameRuleKind = 'fixed' | 'any' | 'prefix';

export const getPairingSourceId = (pairing: AllowedPairing): string =>
  (pairing.source_id ?? pairing.source_type ?? '').trim();

export const getPairingTargetId = (pairing: AllowedPairing): string =>
  (pairing.target_id ?? pairing.target_type ?? '').trim();

export const expandTypePattern = (value: string): string[] => {
  if (!value) {
    return [];
  }
  return value
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const matchesTypePattern = (pattern: string, actual: string): boolean => {
  const normalizedPattern = pattern.trim();
  const normalizedActual = actual.trim();
  if (!normalizedPattern || !normalizedActual) {
    return false;
  }
  if (normalizedPattern === '*') {
    return true;
  }
  return normalizedPattern.toLowerCase() === normalizedActual.toLowerCase();
};

export const expandTypeTargets = (pattern: string, allNodeTypeIds: string[]): string[] => {
  const normalizedPattern = pattern.trim();
  if (!normalizedPattern) {
    return [];
  }
  if (normalizedPattern === '*') {
    return allNodeTypeIds;
  }
  return [normalizedPattern];
};

export const getRelationshipNameRuleKind = (ruleName: string): RelationshipNameRuleKind => {
  const trimmed = ruleName.trim();
  if (trimmed === '*') {
    return 'any';
  }
  if (trimmed.endsWith('*')) {
    return 'prefix';
  }
  return 'fixed';
};

export const getRelationshipPrefix = (ruleName: string): string =>
  ruleName.trim().endsWith('*') ? ruleName.trim().slice(0, -1) : '';

export const isValidRelationshipName = (value: string): boolean =>
  RELATIONSHIP_NAME_PATTERN.test(value.trim());

export const resolveRelationshipNameFromRule = (ruleName: string, candidate: string): { value?: string; error?: string } => {
  const kind = getRelationshipNameRuleKind(ruleName);
  if (kind === 'fixed') {
    return { value: ruleName.trim() };
  }

  const normalizedCandidate = candidate.trim();
  if (!normalizedCandidate) {
    return { error: 'Relationship name is required.' };
  }
  if (!isValidRelationshipName(normalizedCandidate)) {
    return { error: 'Relationship name must match CAPITAL_LETTERS_SEPARATED_WITH_UNDERSCORES.' };
  }

  if (kind === 'any') {
    return { value: normalizedCandidate };
  }

  const prefix = getRelationshipPrefix(ruleName);
  if (!normalizedCandidate.startsWith(prefix) || normalizedCandidate.length <= prefix.length) {
    return { error: `Relationship name must start with ${prefix}.` };
  }

  return { value: normalizedCandidate };
};

export const relationshipToPairings = (relationship: RelationshipType): AllowedPairing[] => {
  if (Array.isArray(relationship.allowed_pairings) && relationship.allowed_pairings.length > 0) {
    return relationship.allowed_pairings.map((pairing) => ({
      ...pairing,
      source_id: getPairingSourceId(pairing),
      target_id: getPairingTargetId(pairing),
    }));
  }

  const sourceId = (relationship.source_id ?? relationship.source_type ?? '').trim();
  const targetId = (relationship.target_id ?? relationship.target_type ?? '').trim();
  if (!sourceId || !targetId) {
    return [];
  }

  return [
    {
      source_id: sourceId,
      target_id: targetId,
      multiplicity: relationship.multiplicity,
    },
  ];
};
